import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { getReferralCode } from 'store/account';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import { copyToClipboard, isInMobileApp, sendMobileAppCommand } from 'helpers';

import { Card, Icon, Typography } from 'components';

import styles from './ReferralWidget.module.css';

export const ReferralWidget = () => {
  const { trackEvent } = useAnalytics();
  const referralCode = useSelector(getReferralCode);
  const messageToShare = `Check out Tiny Health, the most comprehensive at-home test to help you understand and optimize your gut health. Use my discount code ${referralCode} at checkout for $40 off your first order: https://www.tinyhealth.com/store/`;

  const onShareClick = async () => {
    if (!referralCode) {
      toast.error('Failed to share');
      return;
    }
    trackEvent('Share Clicked', { location: 'homepage' });
    trackEvent('Referral Shared', { method: 'share' });
    try {
      if (navigator?.share) {
        await navigator.share({
          text: messageToShare,
        });
      } else {
        await navigator.clipboard.writeText(messageToShare);
        toast.success('Message copied to clipboard');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to share');
    }
  };

  const copyReferralCode = useCallback(() => {
    if (referralCode) {
      copyToClipboard(referralCode);
      trackEvent('Clicked Button', {
        text: 'Copy code',
        href: '',
        variety: 'Button',
      });
      trackEvent('Referral Shared', { method: 'code_copy' });
      toast.success('Referral code copied to clipboard');
    }
  }, [referralCode, trackEvent]);

  const sendEmail = useCallback(async () => {
    trackEvent('Clicked Button', {
      text: 'Send Email',
      href: '',
      variety: 'Button',
    });
    trackEvent('Referral Shared', { method: 'email' });
    const subject = `$40 off gut test with my code`;
    const mailto = `mailto:?subject=${encodeURIComponent(
      subject,
    )}&body=${encodeURIComponent(messageToShare)}`;
    // TEMP-WRAPPER: in-app the wrapper opens mailto: natively (WKWebView drops
    // it silently otherwise); builds without the command get the share sheet.
    if (sendMobileAppCommand('open-url', { url: mailto, shareText: messageToShare })) {
      return;
    }
    if (isInMobileApp() && navigator?.share) {
      try {
        await navigator.share({ title: subject, text: messageToShare });
        return;
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
      }
    }
    window.location.href = mailto;
  }, [messageToShare, trackEvent]);

  if (!referralCode) return <></>;
  return (
    <Card variant='wrapper' className={styles.referralWidgetContainer}>
      <div className={styles.referralWidgetContent}>
        <div className={styles.referralWidgetHeader}>
          <div className={styles.referralWidgetIcon}>
            <Icon name='gift' />
          </div>
          <div className='flex flex-column gap-2'>
            <Typography variant='heading-m'>
              {`Invite a friend and you'll both get $40`}
            </Typography>
            <Typography variant='label'>
              {`Invite your friends to give them $40 off their kit and get $40 in
              store credit you can apply to anything you'd like when they use
              this code:`}
            </Typography>
          </div>
        </div>
        <div className='w-100'>
          <div className={styles.referralWidgetActions}>
            <div className={styles.referralWidgetButton} onClick={onShareClick}>
              <Typography variant='body-s'>Share</Typography>
              <Icon name='share' size='s' />
            </div>
            <div className={styles.referralWidgetButton} onClick={sendEmail}>
              <Typography variant='body-s'>Send Email</Typography>
              <Icon name='mail' size='s' />
            </div>
            <div
              className={styles.referralWidgetButton}
              onClick={copyReferralCode}
            >
              <Typography variant='body-s'>{referralCode}</Typography>
              <Icon name='copy' size='s' />
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};
